import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { withBuildingScope } from "../ui/building-scope";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateOnly, formatDateTime } from "../dates";

/** Original and return time use their canonical writers; no report is resubmitted here. */
export function InspectionActualEffort({
  reportId,
  building,
  disabled = false,
  onPending,
}: {
  reportId: string;
  building: string;
  disabled?: boolean;
  onPending?: (pending: boolean) => void;
}) {
  const [data, setData] = useState<any>(),
    [minutes, setMinutes] = useState(""),
    [day, setDay] = useState(""),
    [reason, setReason] = useState(""),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [pendingView, setPendingView] = useState(false);
  const pending = useRef<
      { path: string; body: string; confirmed: boolean } | undefined
    >(undefined),
    running = useRef(false),
    path = withBuildingScope(
      "/api/operations/visit-reports/" + reportId + "/effort",
      building,
    );
  const dirty =
    !!data &&
    (minutes !==
      (data.actualMinutes === null ? "" : String(data.actualMinutes)) ||
      day !== (data.actualOn ?? "") ||
      reason !== "");
  const load = async () => {
    const next = await api<any>(path);
    setData(next);
    setMinutes(next.actualMinutes === null ? "" : String(next.actualMinutes));
    setDay(next.actualOn ?? "");
    setReason("");
    return next;
  };
  useEffect(() => {
    let current = true;
    api<any>(path)
      .then((next) => {
        if (current) {
          setData(next);
          setMinutes(
            next.actualMinutes === null ? "" : String(next.actualMinutes),
          );
          setDay(next.actualOn ?? "");
        }
      })
      .catch((e) => {
        if (current) setError(e.message);
      });
    return () => {
      current = false;
    };
  }, [path]);
  useEffect(() => {
    onPending?.(dirty || pendingView);
    return () => onPending?.(false);
  }, [dirty, pendingView, onPending]);
  async function retry() {
    if (running.current || !pending.current) return;
    running.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (!pending.current.confirmed) {
        try {
          await api(pending.current.path, {
            method: "POST",
            body: pending.current.body,
          });
          pending.current.confirmed = true;
        } catch (e: any) {
          if (e.statusCode >= 400 && e.statusCode < 500) {
            pending.current = undefined;
            setPendingView(false);
          }
          throw e;
        }
      }
      await load();
      pending.current = undefined;
      setPendingView(false);
      setNotice(
        "Actual time saved. The submitted report and planned duration are unchanged.",
      );
    } catch (e: any) {
      setError(
        (pending.current?.confirmed
          ? "Actual time saved. Reload its retained result: "
          : "") + e.message,
      );
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  function save() {
    if (!data || pending.current || running.current) return;
    const original = data.kind === "Original",
      body = {
        idempotencyKey: crypto.randomUUID(),
        submissionId: data.submissionId,
        actualMinutes: Number(minutes),
        actualOn: day,
        ...(original
          ? {
              expectedVersion: data.version,
              expectedReportVersion: data.reportVersion,
              plannedMinutes: data.plannedMinutes,
              reason,
            }
          : {
              expectedVersion: data.reportVersion,
              expectedEffortVersion: data.version,
            }),
      };
    pending.current = {
      path: withBuildingScope(
        original
          ? "/api/operations/executions/" + data.executionId + "/effort"
          : "/api/operations/visit-reports/" + reportId + "/effort",
        building,
      ),
      body: JSON.stringify(body),
      confirmed: false,
    };
    setPendingView(true);
    void retry();
  }
  return (
    <section aria-label="Actual inspection time" className="report-no-print">
      <h2>Actual inspection time</h2>
      <p>
        The report is submitted separately. Enter the time actually spent and
        the work date; planned duration is retained. The PM should review the
        timings after two inspections.
      </p>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {!data && !error && (
        <p role="status">Loading recorded inspection time…</p>
      )}
      {pendingView && !busy && (
        <button type="button" disabled={disabled} onClick={() => void retry()}>
          {pending.current?.confirmed
            ? "Reload saved actual time"
            : "Retry retained actual time save"}
        </button>
      )}
      {data && (
        <>
          <p>
            {data.actualMinutes === null
              ? "Actual time has not been recorded."
              : data.actualMinutes +
                " minutes on " +
                formatDateOnly(data.actualOn) +
                "."}{" "}
            {data.programmeMinutes !== null
              ? "Planned source duration: " +
                data.programmeMinutes +
                " minutes."
              : ""}
          </p>
          {!data.writesEnabled && (
            <p>Inspection time changes are not enabled.</p>
          )}
          {data.writesEnabled && !data.canRecord && (
            <p>
              Recording time requires current officer or PM/Operations
              coordination authority, submitted evidence and the current source
              and attendance.
            </p>
          )}
          {data.canRecord && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                save();
              }}
            >
              <fieldset disabled={disabled || busy || pendingView}>
                <legend>
                  {data.actualMinutes === null
                    ? "Record actual time"
                    : "Correct actual time"}
                </legend>
                <div className="report-grid">
                  <label>
                    Actual inspection minutes
                    <input
                      required
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={10080}
                      value={minutes}
                      onChange={(e) => setMinutes(e.target.value)}
                    />
                  </label>
                  <label>
                    Actual inspection work date
                    <DateOnlyInput
                      required
                      value={day}
                      onChange={(v) => setDay(v ?? "")}
                    />
                  </label>
                </div>
                {data.kind === "Original" && (
                  <label>
                    Time review note (optional)
                    <textarea
                      maxLength={2000}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </label>
                )}
                <button disabled={minutes === "" || !day}>
                  Save actual inspection time
                </button>
              </fieldset>
            </form>
          )}
          {data.history.length > 0 && (
            <details>
              <summary>Actual time history</summary>
              {data.history.map((h: any, i: number) => (
                <p key={i}>
                  {formatDateTime(h.occurred_at)} · {h.new_value.actualMinutes}{" "}
                  minutes on {formatDateOnly(h.new_value.actualOn)} · {h.reason}
                </p>
              ))}
            </details>
          )}
        </>
      )}
    </section>
  );
}
